import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/Store";
import { apiError, http, imgUrl, priceLabel } from "@/lib/api";
import ProductForm from "./ProductForm";

export default function AdminProducts() {
  const { categories, reload } = useStore();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => http.get("/admin/products").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); reload(); };

  const save = async (p) => {
    const { id, slug, order, created_at, ...body } = p;
    try {
      if (id) await http.put(`/admin/products/${id}`, body); else await http.post("/admin/products", body);
      toast.success("Produto guardado"); setEditing(null); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (p) => save({ ...p, visible: !p.visible });
  const remove = async (p) => { if (!window.confirm(`Apagar "${p.name}"?`)) return; await http.delete(`/admin/products/${p.id}`); refresh(); };
  const move = async (i, d) => {
    const next = [...items]; const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]]; setItems(next);
    await http.post("/admin/products/reorder", { ids: next.map((x) => x.id) }); reload();
  };
  const catName = (p) => { const c = categories.find((x) => x.slug === p.category); return `${c?.title || p.category} · ${c?.subcategories.find((s) => s.slug === p.subcategory)?.name || p.subcategory}`; };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-3xl font-bold">Produtos <span className="text-base text-[var(--muted-ink)]">({items.length})</span></h2>
        <button data-testid="admin-new-product-button" className="btn-primary" onClick={() => setEditing({})}><Plus className="h-4 w-4" />Novo produto</button>
      </div>
      <ul className="mt-6 space-y-3">
        {items.map((p, i) => (
          <li key={p.id} data-testid={`admin-product-row-${p.slug}`} className={`flex items-center gap-4 rounded-2xl border border-[var(--line)] bg-white p-3 ${p.visible ? "" : "opacity-60"}`}>
            <div className="flex flex-col"><button onClick={() => move(i, -1)} data-testid={`admin-product-up-${p.slug}`} className="p-1 hover:text-[var(--violet)]"><ArrowUp className="h-4 w-4" /></button><button onClick={() => move(i, 1)} data-testid={`admin-product-down-${p.slug}`} className="p-1 hover:text-[var(--violet)]"><ArrowDown className="h-4 w-4" /></button></div>
            <img src={imgUrl(p.images?.[0])} alt="" className="h-16 w-16 rounded-xl bg-[var(--lilac)] object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{p.name} {p.needs_review && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"><AlertCircle className="h-3 w-3" />Rever textos</span>}</p>
              <p className="truncate text-xs text-[var(--muted-ink)]">{catName(p)}</p>
            </div>
            <span className="hidden text-sm font-bold text-[var(--violet)] sm:block">{priceLabel(p)}</span>
            <button onClick={() => toggle(p)} data-testid={`admin-product-toggle-${p.slug}`} className="rounded-full p-2 hover:bg-[var(--lilac)]" title={p.visible ? "Ocultar" : "Mostrar"}>{p.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
            <button onClick={() => setEditing(p)} data-testid={`admin-product-edit-${p.slug}`} className="rounded-full p-2 hover:bg-[var(--lilac)]"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(p)} data-testid={`admin-product-delete-${p.slug}`} className="rounded-full p-2 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      {editing && <ProductForm initial={editing} categories={categories} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
